import get from "lodash/get";
import set from "lodash/set";
import has from "lodash/has";
import omit from "lodash/omit";
import size from "lodash/size";
import keyBy from "lodash/keyBy";
import isNil from "lodash/isNil";
import isEmpty from "lodash/isEmpty";
import omitBy from "lodash/omitBy";
import mapValues from "lodash/mapValues";
import { compareKeys, modify } from "mythil/object";
import { getModelTitle, } from "~/stateGetter";
import { StateGetterBase } from "./StateGetterBase";
import { getUniqueKey } from "~/lib/uniqueId";
import { presentFilters, presentAppliedFilterValuesObject } from "~/statePresenter";
import { isDateFilter } from "~/lib/filterTypes";
import { getAllResultStateInfos, getCalendarViews, getPredefinedViews, getResultInfo } from "../stateGetter";

const EMPTY_OBJECT = {}
export class FilteredViewState extends StateGetterBase {
    get activeFilteredViewName() {
        return get(this.getModel(), `settings.activeFilteredView`);
    }

    get activeViewSort() {
        return get(this.getWorkingView(), `sortOptions`, EMPTY_OBJECT);
    }

    get activeViewFilterOrder() {
        return get(this.getModel(), `settings.filterOrder`, EMPTY_OBJECT);
    }

    get activeViewColumnVisibility() {
        return get(this.getWorkingView(), `columnVisibility`, EMPTY_OBJECT);
    }

    get activeViewCalendarViewConfig() {
        return get(this.getWorkingView(), `calendarViewConfig`, EMPTY_OBJECT);
    }

    get count() {
        return size(this.getFilteredViews());
    }

    get countAll() {
        return size({ ...this.getFilteredViews(), ...this.getSharedViews() });
    }

    get hasChanged() {
        const calendarFilter = this.getCalendarConfig().enabled && get(getCalendarViews(this.state, this.modelId), "0.filter_name");
        const nestedCompare = (prev, current, key) => {
            if (prev === current) { return true; }
            if (key === "createdAt" && (isNil(prev) || isNil(current))) { return true; }
            if (key === "created_at" && typeof prev !== "object" && typeof current !== "object") { return true; }
            if (typeof prev !== typeof current) { return false; }
            if (typeof prev !== "object") { return prev === current; }
            if (key === "filters") {
                prev = this.getCleanedWorkingFilters(prev);
                current = this.getCleanedWorkingFilters(current);
                if (calendarFilter && key === "filters") {
                    return nestedCompare(omit(prev, calendarFilter), omit(current, calendarFilter))
                }
            }

            return compareKeys(prev, current, nestedCompare);
        }

        if (!this.getOwnActiveView()) {
            return true;
        }

        const existingFilteredViews = get(this.state.ui.settings, `${this.tableSettingsIdentifier}`, {});
        if (!existingFilteredViews.filteredViews) {
            return this.getWorkingView().hasChanged;
        }


        return !nestedCompare(
            get(existingFilteredViews, `filteredViews.${this.activeFilteredViewName}`),
            get(this.getMergedSettings(), `${this.tableSettingsIdentifier}.filteredViews.${this.activeFilteredViewName}`)
        );
    }

    isSharedView(view = this.activeFilteredViewName) {
        return !has(this.getFilteredViews(), view);
    }

    getFilteredViews() {
        return get(this.getModel(), `settings.filteredViews`, EMPTY_OBJECT);
    }
    
    getSharedViews() {
        return get(this.state, `ui.sharedViews.${this.tableSettingsIdentifier}`, EMPTY_OBJECT);
    }
    
    getDefaultViews() {
        return getPredefinedViews(this.state, this.modelId);
    }
    
    getCalendarConfig() {
        return this.activeViewCalendarViewConfig;
    }
    
    getColumnVisibility() {
        return this.activeViewColumnVisibility;
    }

    getSortOrder() {
        return this.activeViewSort;
    }

    getFilterOrder() {
        return this.activeViewFilterOrder;
    }

    getViewOrDefault (viewName, defaultView) {
        return get(this.getFilteredViews(), viewName) || get(this.getSharedViews(), viewName) || get(this.getDefaultViews(), viewName) || defaultView;
    }

    getView (viewName, defaultValue = EMPTY_OBJECT) {
        return this.getViewOrDefault(viewName, defaultValue);
    }

    getActiveView() {
        return this.getViewOrDefault(this.activeFilteredViewName);
    }

    getWorkingView() {
        return this.getModel().activeView
    }

    getCleanWorkingView(config) {
        let view = omit(this.getWorkingView(), "sort", "previousTitle", "shouldSetPath", "key", "hasChanged");

        if (get(config, "omitFilters")) {
            set(view, "filters", undefined);
        }

        if (!view.filters) {
            return view;
        }
        
        return modify(view, `filters`, this.getCleanedWorkingFilters());
    }

    getCleanedWorkingFilters(filters = this.getWorkingView()?.filters) {
        if (!filters) {
            return;
        }

        const resultInfo = getAllResultStateInfos(this.state, this.modelId);
        const filterValues = presentAppliedFilterValuesObject({
            ...resultInfo,
            itemId: null,
            filters: keyBy(presentFilters(resultInfo), "name"),
            filterValues: omitBy(filters, (filterValue) => isNil(filterValue)),
        })

        if (isEmpty(filterValues)) return;

        return mapValues(
            presentAppliedFilterValuesObject({
                ...resultInfo,
                itemId: null,
                filterValues,
                filters: keyBy(presentFilters(resultInfo), "name"),
            }),
            (filter) => omitBy({
                ...filter,
                value: isDateFilter(filter.filterType) ? undefined : filter.value
            }, x => x === undefined)
        )
    }

    getOwnActiveView() {
        return get(this.getFilteredViews(), this.activeFilteredViewName)
    }

    getMergedSettings(config) {
        const activeView = this.getCleanWorkingView(config);
        const activeKey = this.getOwnActiveView() ? activeView.viewKey : getUniqueKey()
        const identifier = this.tableSettingsIdentifier;
        const originalSettings = get(this.state, ["ui", "settings"]);

        let filteredViews = this.getModel().settings?.filteredViews;
            
        filteredViews = modify(filteredViews, `${activeKey}`, activeView)
        filteredViews = modify(filteredViews, `${activeKey}.viewKey`, activeKey)
        filteredViews = modify(filteredViews, `${activeKey}.isShared`, activeView.isShared && activeKey === activeView.viewKey)
        // filteredViews = modify(filteredViews, `${activeKey}.filters`, activeView.filters && mapValues(
        //     presentAppliedFilterValuesObject({
        //         ...getAllResultParams(this.state, this.modelId),
        //         filterValues: activeView.filters,
        //     }),
        //     (filter) => omitBy({
        //         ...filter,
        //         value: isDateFilter(filter.filterType) ? undefined : filter.value
        //     }, x => x === undefined)
        // ))


        filteredViews = modify(filteredViews, `${activeKey}`, (view) => omitBy(view, (x) => isNil(x)))


        return {
            ...originalSettings,
            [identifier]: {
                activeFilteredView: activeKey,
                filteredViews
            }
        }
    }

    getFilteredViewProps() {
        return {
            views: this.getFilteredViews(),
            sharedViews: this.getSharedViews(),
            defaultViews: this.getDefaultViews(),
            modelName: this.modelName
        }
    }

    getFilteredViewTitle() {
        const filteredViews = this.getFilteredViews(),
              activeFilteredView = this. activeFilteredViewName;

        return this.getView(activeFilteredView)?.title || filteredViews[activeFilteredView]?.previousTitle || getModelTitle(this.state, this.modelId)
    }
}


export const getFitleredViewState = (state, modelId) =>  new FilteredViewState(state, modelId)